/**
 * TraineeManagementPage — main shell for the Trainee Management module.
 *
 * Three tabs:
 *   1. Trainees       — table list with search/filter, CRUD, import/export
 *   2. Batches        — batch CRUD
 *   3. Field Settings — dynamic form builder (org_admin+ to edit)
 *
 * Top-level page (added to sidebar under DELIVER as "Trainees").
 */
import { useState } from 'react';
import { Users, Boxes, Settings as SettingsIcon, CalendarDays, Award, BadgeCheck, Trophy, MessageSquare, BookOpen, Gauge } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import TraineesTab from './TraineesTab';
import BatchesTab from './BatchesTab';
import FieldSettingsTab from './FieldSettingsTab';
import AttendanceTab from './AttendanceTab';
import CertificationsTab from './CertificationsTab';
import LeaderboardTab from './LeaderboardTab';
import FeedbackTab from './FeedbackTab';
import LearningTab from './LearningTab';
import AssessmentsSection from './AssessmentsSection';
import ReadinessTab from './ReadinessTab';
import { TraineeFilterProvider } from './TraineeFilterContext';
import { useAuth } from '../../contexts/AuthContext';

export default function TraineeManagementPage() {
  const [tab, setTab] = useState('trainees');
  const { hasPermission } = useAuth();

  return (
    <TraineeFilterProvider>
    <div className="space-y-6" data-testid="page-trainee-management">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight" data-testid="trainee-management-heading">
          Trainee Management
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Manage batches, trainees, attendance and certification — all in one place.
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList data-testid="trainee-tabs">
          <TabsTrigger value="trainees" data-testid="tab-trainees">
            <Users className="w-4 h-4 mr-2" /> Trainees
          </TabsTrigger>
          <TabsTrigger value="batches" data-testid="tab-batches">
            <Boxes className="w-4 h-4 mr-2" /> Batches
          </TabsTrigger>
          <TabsTrigger value="attendance" data-testid="tab-attendance">
            <CalendarDays className="w-4 h-4 mr-2" /> Attendance
          </TabsTrigger>
          <TabsTrigger value="assessments" data-testid="tab-assessments">
            <Award className="w-4 h-4 mr-2" /> Assessments
          </TabsTrigger>
          <TabsTrigger value="learning" data-testid="tab-learning">
            <BookOpen className="w-4 h-4 mr-2" /> Learning
          </TabsTrigger>
          <TabsTrigger value="certifications" data-testid="tab-certifications">
            <BadgeCheck className="w-4 h-4 mr-2" /> Certifications
          </TabsTrigger>
          {hasPermission('readiness.view') && (
            <TabsTrigger value="readiness" data-testid="tab-readiness">
              <Gauge className="w-4 h-4 mr-2" /> Readiness
            </TabsTrigger>
          )}
          <TabsTrigger value="leaderboard" data-testid="tab-leaderboard">
            <Trophy className="w-4 h-4 mr-2" /> Leaderboard
          </TabsTrigger>
          <TabsTrigger value="feedback" data-testid="tab-feedback">
            <MessageSquare className="w-4 h-4 mr-2" /> Feedback
          </TabsTrigger>
          <TabsTrigger value="fields" data-testid="tab-fields">
            <SettingsIcon className="w-4 h-4 mr-2" /> Field Settings
          </TabsTrigger>
        </TabsList>

        <TabsContent value="trainees" className="mt-4">
          <TraineesTab />
        </TabsContent>
        <TabsContent value="batches" className="mt-4">
          <BatchesTab />
        </TabsContent>
        <TabsContent value="attendance" className="mt-4">
          <AttendanceTab />
        </TabsContent>
        <TabsContent value="assessments" className="mt-4">
          <AssessmentsSection />
        </TabsContent>
        <TabsContent value="learning" className="mt-4">
          <LearningTab />
        </TabsContent>
        <TabsContent value="certifications" className="mt-4">
          <CertificationsTab />
        </TabsContent>
        <TabsContent value="readiness" className="mt-4">
          <ReadinessTab />
        </TabsContent>
        <TabsContent value="leaderboard" className="mt-4">
          <LeaderboardTab />
        </TabsContent>
        <TabsContent value="feedback" className="mt-4">
          <FeedbackTab />
        </TabsContent>
        <TabsContent value="fields" className="mt-4">
          <FieldSettingsTab />
        </TabsContent>
      </Tabs>
    </div>
    </TraineeFilterProvider>
  );
}
